export type PlanReviewDetectionStrategy = "text_only" | "text_plus_vision" | "vision_only";
export type PlanReviewChecklistCategory = "residential_site_plan" | "lot_split" | "lot_combination";
export type PlanReviewDetectionProfile = {
  routing_mode: "auto" | "text_only" | "text_then_vision" | "vision_first";
  concepts: string[];
  text_aliases: string[];
  visual_cues: string[];
  negative_cues: string[];
  minimum_evidence: "any_relevant_match" | "one_clear_match" | "labeled_value" | "numeric_value";
  preferred_sources: Array<"text" | "dimensional_fact" | "structured_extract" | "vision">;
};
export type PlanReviewCorrectionReason =
  | "ocr_miss"
  | "visual_only"
  | "wrong_page"
  | "wrong_bbox"
  | "bad_alias"
  | "model_false_positive";

export type ChecklistRowApplicability = "required" | "optional" | "not_applicable";

export type ChecklistItem = {
  // Absent on a row the planner added in the confirm editor; the server
  // derives one from the label at confirm.
  key?: string;
  label: string;
  required: boolean;
  verification_mode: "presence_only" | "presence_with_value" | "numeric_rule";
  detection_strategy?: PlanReviewDetectionStrategy;
  detection_profile?: PlanReviewDetectionProfile;
  numeric_rule?: { operator: "gt" | "gte" | "lt" | "lte" | "eq"; value: number };
  // Proposal fields, present on rows an extraction proposed from an upload:
  // where the row sat in the source, how the source marked it, and whether
  // the model proposed it or it was copied straight from the document
  // because the model skipped it.
  position?: number;
  section?: string | null;
  applicability?: ChecklistRowApplicability;
  source_text?: string | null;
  origin?: "model" | "source" | "planner";
  // Set on a long bulleted note the model skipped and a second check put
  // back as a requirement (ENG-2100), or on a sentence of running text the
  // same check put back (ENG-2101): that check's one-clause reason.
  origin_reason?: string | null;
  // How the document marked a row copied from it: "checkbox", "bullet",
  // ..., or "prose" for a sentence of running text (ENG-2101).
  source_kind?: string | null;
  // Look-for guidance the research pass wrote from the municipality's own
  // documents (ENG-2004), editable by the planner, and the documents the
  // guidance cites. Empty citations mean the text is general guidance
  // from the row's wording alone.
  look_for?: string | null;
  citations?: ChecklistRowCitation[];
  // The key of the row this one sits under in the source document
  // (ENG-2162): "Water service" under "written confirmation of applicable
  // utilities". Rows stay a flat list; null or absent on a top-level row.
  parent_key?: string | null;
};

export type ChecklistRowCitation = {
  document_id: number;
  document_name: string;
  chunk_id?: number | null;
};

/** How the research pass went over the proposal: what it wrote and what it could cite. */
export type ChecklistResearchSummary = {
  status: "complete" | "partial" | "failed";
  researched: number;
  sourced: number;
  retrieval_errors?: number;
};

/**
 * One model call over the bulleted notes left out of a proposal. "partial"
 * means some lines got no verdict (past the cap, or the model left them
 * out); "failed" means the check did not run and the notes stayed out.
 */
export type ChecklistNotesCheck = {
  status: "complete" | "partial" | "failed";
  checked: number;
  // Lines the check itself judged requirements and put back.
  restored: number;
  // Short sub-bullets that came back in with a restored note above them.
  restored_with_parent?: number;
  unchecked: number;
  verdicts?: Array<{ text: string; section?: string | null; verdict: "requirement" | "note"; reason: string }>;
};

/** How many of the document's own item lines the proposal covers. */
export type ChecklistProposalCoverage = {
  source_rows: number;
  matched: number;
  added_from_source: number;
  // Long bulleted notes the model skipped that are still out of the rows.
  notes_left_out?: number;
  // The second check on those notes (ENG-2100); absent when none were
  // skipped and on proposals from before the check existed.
  notes_check?: ChecklistNotesCheck | null;
  // Sentences of the document's running text that read like requirements
  // (ENG-2101): how many were found, covered by a proposed row, put back
  // after the second check judged them requirements, and left out. All
  // absent when the document had none, and on older proposals. prose_check
  // is that check over the uncovered ones; it has no restored_with_parent.
  prose_candidates?: number;
  prose_matched?: number;
  prose_restored?: number;
  prose_left_out?: number;
  prose_check?: ChecklistNotesCheck | null;
  structure: "list" | "none";
  // Model rows taken out before review because they were only a blank or a
  // bare answer option lifted out of a question (ENG-2196); absent on
  // proposals from before the filter existed.
  dropped_rows?: number;
  // Checkbox marks counted in the PDF's own text layer (null for scans and
  // text uploads), and how many more of them there are than the parser
  // returned marked lines: loss the row guarantee cannot see.
  text_layer_markers?: number | null;
  parser_shortfall?: number | null;
  // A scan has no text layer, so a vision model counts its item marks
  // instead (ENG-2102). marker_witness says which count the shortfall was
  // measured against; vision_check is "failed" when the scan's count could
  // not run, and absent when it did not apply.
  marker_witness?: "text_layer" | "vision" | null;
  vision_markers?: number | null;
  vision_check?: "counted" | "failed" | null;
  // Where the rows' parent links came from (ENG-2162): the PDF's text
  // layer, the vision model reading the page images, or nowhere (every row
  // top-level). dropped counts model links that broke the nesting rules.
  nesting?: {
    source: "text_layer" | "vision" | "none";
    status: string;
    links: number;
    dropped?: number;
  } | null;
};

export type PlanReviewEvidence = {
  page?: number;
  source?: string;
  excerpt?: string;
  bbox?: number[] | null;
  confidence?: number;
  source_type?: string;
  matched_label?: string;
  lexical_score?: number;
  semantic_score?: number;
  localized?: boolean;
  page_link_only?: boolean;
};

export type PlanReviewBoundingBox = {
  page?: number;
  bbox?: number[] | null;
  confidence?: number;
  label?: string;
  focusKey?: string;
  stage?: "finder" | "extracted";
  stream?: boolean;
};

export type PlanReviewDimensionalFact = {
  id?: string;
  page?: number;
  value?: number;
  unit?: string;
  label?: string;
  statement?: string;
  snippet?: string;
  bbox?: number[] | null;
  confidence?: number;
  searchable_text?: string;
  /** e.g. reducto_parse, vision_recovery */
  extraction_source?: string;
  /** e.g. scalar_dimension, bearing_length, vision_recovered */
  fact_kind?: string;
  measurement_recovery?: Record<string, unknown>;
};

export type PlanReviewChecklistResult = {
  key: string;
  label: string;
  required: boolean;
  verification_mode: "presence_only" | "presence_with_value" | "numeric_rule";
  detection_strategy?: PlanReviewDetectionStrategy;
  detection_profile?: PlanReviewDetectionProfile;
  status: "present" | "missing" | "uncertain" | "pending";
  reason?: string;
  explanation?: string;
  extracted_value?: string | number | null;
  numeric_rule?: { operator: "gt" | "gte" | "lt" | "lte" | "eq"; value: number } | null;
  evidence?: PlanReviewEvidence[];
  confidence?: number;
  evidence_sources?: string[];
  machine_source?: string;
  diagnostics?: Record<string, unknown>;
  code_references?: string[];
  edited?: boolean;
  feedback_id?: number;
  correction_reason?: PlanReviewCorrectionReason | null;
  correction_note?: string | null;
};

export type PlanReviewSummary = {
  checklist_items: {
    total: number;
    present: number;
    missing: number;
    uncertain: number;
    edited: number;
  };
  extracted_facts: {
    total: number;
    evidence_matches: number;
    pages_with_evidence: number;
  };
  detected_issues: {
    total: number;
    missing_required: number;
    uncertain_required: number;
  };
  completeness?: {
    score: number;
    pass: boolean;
    required_total: number;
    required_present: number;
    required_missing: number;
    required_uncertain: number;
    threshold: number;
    version: number;
  };
};

export type PlanReviewOrdinanceOutcome = "compliant" | "non_compliant" | "needs_review";

export type PlanReviewOrdinanceComplianceItem = {
  id: string;
  topic: string;
  plan_value_text: string;
  source_type?: string;
  page?: number | null;
  ordinance_question?: string | null;
  ordinance_answer?: string | null;
  citations?: Array<{ citation: string; excerpt: string }>;
  outcome: PlanReviewOrdinanceOutcome;
  explanation?: string;
  ordinance_requirement_summary?: string;
  /** Present when outcome was computed from a compiled RulePack rule */
  matched_rule_id?: string | null;
  /** How this row was adjudicated (structured RulePack vs LLM retrieval, etc.) */
  adjudication_source?: "structured_rule" | "llm" | "retrieval_failed" | "error" | "none" | string;
};

export type PlanReviewOrdinanceComplianceProgress = {
  completed: number;
  total: number;
  current_topic?: string | null;
  current_checklist_key?: string | null;
  zoning_key?: string;
  code_year?: number;
  cache_hit?: boolean;
  rule_pack_id?: number;
  rules_compiled_so_far?: number;
  rule_pack_error?: string;
  /** Backend-driven phase for finer-grained UI while a topic is in flight */
  phase?:
    | "starting"
    | "researching"
    | "adjudicating"
    | "loading_rule_pack"
    | "reusing_rule_pack"
    | "compiling_rule_pack"
    | "rule_pack_unavailable"
    | string;
};

export type PlanReviewOrdinanceRulePackMeta = {
  id: number;
  compiler_version: string;
  source_fingerprint: string;
  code_year: number;
  zoning_key: string;
  status: string;
  rules_count: number;
};

export type PlanReviewOrdinanceCompliance = {
  status?: "pending" | "processing" | "complete" | "failed";
  requested_at?: string;
  started_at?: string;
  completed_at?: string;
  last_heartbeat_at?: string;
  error_message?: string | null;
  source_document_ids?: number[];
  source_document_names?: string[];
  progress?: PlanReviewOrdinanceComplianceProgress;
  summary?: {
    compliant: number;
    non_compliant: number;
    needs_review: number;
    skipped: number;
  };
  items?: PlanReviewOrdinanceComplianceItem[];
  /** Metadata for cached structured ordinance rules when RulePack is enabled */
  rule_pack?: PlanReviewOrdinanceRulePackMeta | null;
};

export type PlanReviewDetectedIssueRow = {
  item_key: string;
  label: string;
  status: string;
};

export type PlanReviewResults = {
  checklist_results?: PlanReviewChecklistResult[];
  dimensional_facts?: PlanReviewDimensionalFact[];
  bounding_boxes?: PlanReviewBoundingBox[];
  issues?: string[];
  issue_rows?: PlanReviewDetectedIssueRow[];
  summary?: PlanReviewSummary;
  stage_timings?: Record<string, number>;
  ordinance_compliance?: PlanReviewOrdinanceCompliance;
  [key: string]: unknown;
};

export type PlanReviewItemFeedback = {
  id: number;
  item_key: string;
  original_status?: string | null;
  original_result_snapshot?: Record<string, unknown>;
  corrected_status: "present" | "missing" | "uncertain";
  corrected_explanation?: string | null;
  corrected_value?: string | null;
  corrected_evidence?: PlanReviewEvidence[];
  correction_reason?: PlanReviewCorrectionReason | null;
  note?: string | null;
  updated_at: string;
};

export type PlanReviewChecklist = {
  id: number;
  name: string;
  category: PlanReviewChecklistCategory;
  plan_type_id: number | null;
  items: ChecklistItem[];
  municipality_id: number | null;
  system_default: boolean;
  archived_at?: string | null;
  s3_key?: string | null;
  original_filename?: string | null;
  created_at: string;
};

export type PlanReviewPlanType = {
  id: number;
  municipality_id: number;
  name: string;
  description: string | null;
  review_kind: PlanReviewChecklistCategory;
  // Set once any checklist, archived included, has used the plan type.
  review_kind_locked: boolean;
  archived_at: string | null;
  checklists?: PlanReviewChecklist[];
};

export type PlanReviewListItem = {
  id: number;
  title: string;
  status: "pending" | "processing" | "complete" | "failed";
  municipality_id: number;
  // Set when the review was run from a logged submission; null for the
  // unlogged runs the history sidebar lists.
  submission_id: number | null;
  created_at: string;
  updated_at: string;
};

/**
 * Which reviews the history list asks for. Unlogged is the sidebar's list;
 * there is no logged scope, because a logged review belongs to the
 * municipality queue rather than to one user's history.
 */
export type PlanReviewListScope = "unlogged" | "all";

export type PlanReviewRun = {
  id: number;
  municipality_id: number;
  submission_id?: number | null;
  submission_applicant_email?: string | null;
  submission_project_name?: string | null;
  user_id: number | null;
  checklist_id: number;
  checklist_snapshot: { id?: number; name: string; category: PlanReviewChecklistCategory; items: ChecklistItem[] };
  status: "pending" | "processing" | "complete" | "failed";
  title?: string;
  desired_zoning?: string | null;
  source_plan_filename: string;
  source_plan_url?: string | null;
  viewer_plan_url?: string | null;
  annotated_plan_url?: string | null;
  results: PlanReviewResults;
  effective_results: PlanReviewResults;
  item_feedbacks: PlanReviewItemFeedback[];
  error_message?: string | null;
  created_at: string;
  updated_at: string;
  duration_ms?: number | null;
  current_stage?: string | null;
  started_at?: string | null;
  last_heartbeat_at?: string | null;
  visual_intelligence_status?: PlanReviewVisualIntelligenceStatus;
  visual_intelligence?: PlanReviewVisualIntelligencePayload;
};

export type PlanReviewVisualIntelligenceStatus = "not_started" | "running" | "complete";

export type PlanReviewVisualIntelligencePayload = {
  pages?: Record<string, PlanReviewVisualIntelligencePage>;
  items?: Record<string, PlanReviewVisualIntelligenceItem>;
  error?: {pass?: string; message?: string};
};

export type PlanReviewVisualIntelligencePage = {
  status?: string;
  context_written?: boolean;
  viewer_url?: string;
  width?: number;
  height?: number;
  crop?: {x: number; y: number; width: number; height: number};
  error?: {pass?: string; message?: string};
};

export type PlanReviewVisualIntelligenceItem = {
  pages?: Record<string, PlanReviewVisualIntelligenceItemPage>;
};

export type PlanReviewVisualIntelligenceItemPage = {
  finder_boxes?: Array<{finder?: number; boxes?: Array<{x?: number; y?: number; w?: number; h?: number}>}>;
  extracted?: {crops?: Array<{index?: number; description?: string; boxes?: Array<{x?: number; y?: number; w?: number; h?: number}>}>};
  error?: {pass?: string; message?: string};
};

export type PlanReviewVisualBox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type PlanReviewVisualItemProgressStatus = "pending" | "finding" | "extracting" | "extracted" | "error";

export type PlanReviewVisualItemProgress = {
  key: string;
  status: PlanReviewVisualItemProgressStatus;
  message?: string;
};

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

async function parseOrThrow(response: Response) {
  if (response.ok) return response.json();
  const data = await response.json().catch(() => ({}));
  throw new Error(data.error || `Request failed with ${response.status}`);
}

export async function listPlanReviewChecklists(params: {
  municipalityId: number;
  planTypeId?: number;
  category?: PlanReviewChecklistCategory;
}) {
  const query = new URLSearchParams({ municipality_id: String(params.municipalityId) });
  if (params.planTypeId) {
    query.set("plan_type_id", String(params.planTypeId));
  } else {
    query.set("category", params.category || "residential_site_plan");
  }
  const response = await fetch(`/api/v1/plan_review/checklists?${query.toString()}`);
  return parseOrThrow(response) as Promise<PlanReviewChecklist[]>;
}

export async function listPlanReviewPlanTypes(params: { municipalityId: number }) {
  const query = new URLSearchParams({ municipality_id: String(params.municipalityId) });
  const response = await fetch(`/api/v1/plan_review/plan_types?${query.toString()}`);
  return parseOrThrow(response) as Promise<PlanReviewPlanType[]>;
}

export async function createPlanReviewPlanType(params: {
  municipalityId: number;
  name: string;
  description?: string;
  reviewKind: PlanReviewChecklistCategory;
}) {
  const response = await fetch("/api/v1/plan_review/plan_types", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      municipality_id: params.municipalityId,
      name: params.name,
      description: params.description,
      review_kind: params.reviewKind,
    }),
  });
  return parseOrThrow(response) as Promise<PlanReviewPlanType>;
}

export async function updatePlanReviewPlanType(params: {
  planTypeId: number;
  name?: string;
  description?: string;
  reviewKind?: PlanReviewChecklistCategory;
}) {
  const response = await fetch(`/api/v1/plan_review/plan_types/${params.planTypeId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({
      name: params.name,
      description: params.description,
      review_kind: params.reviewKind,
    }),
  });
  return parseOrThrow(response) as Promise<PlanReviewPlanType>;
}

export async function archivePlanReviewPlanType(planTypeId: number) {
  const response = await fetch(`/api/v1/plan_review/plan_types/${planTypeId}/archive`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  return parseOrThrow(response) as Promise<PlanReviewPlanType>;
}

export async function updatePlanReviewChecklist(params: { checklistId: number; name: string }) {
  const response = await fetch(`/api/v1/plan_review/checklists/${params.checklistId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ name: params.name }),
  });
  return parseOrThrow(response) as Promise<PlanReviewChecklist>;
}

export async function archivePlanReviewChecklist(checklistId: number) {
  const response = await fetch(`/api/v1/plan_review/checklists/${checklistId}/archive`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  return parseOrThrow(response) as Promise<PlanReviewChecklist>;
}

export type ChecklistExtractionStatus = {
  id: number;
  status: "pending" | "processing" | "retrying" | "complete" | "failed";
  municipality_id: number;
  plan_type_id?: number | null;
  category: PlanReviewChecklistCategory;
  original_filename: string;
  // What the planner named the checklist before starting the run (ENG-2140).
  requested_name?: string | null;
  current_stage?: string | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  last_heartbeat_at?: string | null;
  duration_ms?: number | null;
  // Set once the planner confirmed the proposal into a checklist (ENG-2005).
  confirmed_at?: string | null;
  // Set once the planner dropped the proposal (ENG-2140).
  discarded_at?: string | null;
  checklist_id?: number | null;
  // Server-judged: a processing run whose worker stopped heartbeating.
  stalled?: boolean;
  // Server-judged: in flight with no sign of life for STALE_AFTER, queued
  // and retrying runs included (ENG-2206).
  quiet?: boolean;
  row_count?: number;
  result?: {
    name: string;
    category: PlanReviewChecklistCategory;
    items: ChecklistItem[];
    row_count?: number;
    coverage?: ChecklistProposalCoverage;
    research?: ChecklistResearchSummary;
    s3_key?: string;
    original_filename?: string;
  };
};

export async function extractPlanReviewChecklist(params: {
  municipalityId: number;
  file: File;
  planTypeId?: number;
  category?: PlanReviewChecklistCategory;
  name?: string;
}) {
  const form = new FormData();
  form.append("municipality_id", String(params.municipalityId));
  form.append("file", params.file);
  if (params.planTypeId) form.append("plan_type_id", String(params.planTypeId));
  if (params.category) form.append("category", params.category);
  if (params.name) form.append("name", params.name);

  const response = await fetch("/api/v1/plan_review/checklists/extract", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
    body: form,
  });

  return parseOrThrow(response) as Promise<ChecklistExtractionStatus>;
}

export async function getChecklistExtraction(extractionId: number) {
  const response = await fetch(`/api/v1/plan_review/checklists/extractions/${extractionId}`);
  return parseOrThrow(response) as Promise<ChecklistExtractionStatus>;
}

/**
 * The extractions a planner can pick back up (ENG-2140): runs in flight and
 * proposals waiting for review, across the municipality's plan types.
 * Rows are not included; resume one with getChecklistExtraction.
 */
export async function listResumableChecklistExtractions(params: { municipalityId: number }) {
  const response = await fetch(
    `/api/v1/plan_review/checklists/extractions?municipality_id=${encodeURIComponent(params.municipalityId)}`
  );
  return parseOrThrow(response) as Promise<ChecklistExtractionStatus[]>;
}

/** Drops a proposal the planner does not want; it stops being offered as resumable. */
export async function discardChecklistExtraction(extractionId: number) {
  const response = await fetch(`/api/v1/plan_review/checklists/extractions/${extractionId}/discard`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  return parseOrThrow(response) as Promise<ChecklistExtractionStatus>;
}

export type ChecklistExtractionConflictCode = "already_confirmed" | "discarded";

/**
 * Raised when there is nothing left to confirm: the proposal was already
 * saved (checklistId names the checklist) or discarded, here or in another
 * tab.
 */
export class ChecklistExtractionConflictError extends Error {
  code: ChecklistExtractionConflictCode;
  checklistId: number | null;

  constructor(message: string, code: ChecklistExtractionConflictCode, checklistId: number | null) {
    super(message);
    this.name = "ChecklistExtractionConflictError";
    this.code = code;
    this.checklistId = checklistId;
  }
}

// The approval step for a proposal (ENG-2005): the rows as the planner
// reviewed them become the durable checklist under the extraction's plan
// type. Nothing before this call writes a checklist.
export async function confirmChecklistExtraction(params: {
  extractionId: number;
  name: string;
  items: ChecklistItem[];
}) {
  const response = await fetch(`/api/v1/plan_review/checklists/extractions/${params.extractionId}/confirm`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ name: params.name, items: params.items }),
  });
  if (response.status === 409) {
    const data = await response.json().catch(() => ({}));
    const code: ChecklistExtractionConflictCode = data.code === "discarded" ? "discarded" : "already_confirmed";
    throw new ChecklistExtractionConflictError(
      data.error || (code === "discarded" ? "This upload was discarded" : "This upload was already saved as a checklist"),
      code,
      typeof data.checklist_id === "number" ? data.checklist_id : null
    );
  }
  return parseOrThrow(response) as Promise<PlanReviewChecklist>;
}

export async function createPlanReviewRun(params: {
  municipalityId: number;
  checklistId: number;
  file: File;
  title: string;
  desiredZoning?: string;
}) {
  const form = new FormData();
  form.append("municipality_id", String(params.municipalityId));
  form.append("checklist_id", String(params.checklistId));
  form.append("file", params.file);
  form.append("title", params.title.trim());
  if (params.desiredZoning?.trim()) {
    form.append("desired_zoning", params.desiredZoning.trim());
  }

  let response: Response;
  try {
    response = await fetch("/api/v1/plan_review/reviews", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      body: form,
    });
  } catch (cause) {
    // fetch rejects (with a TypeError) only when no response arrived at all:
    // the browser dropped the stream, the network went away. An HTTP error
    // resolves and is handled by parseOrThrow below.
    throw new PlanReviewConnectionError(cause);
  }

  try {
    return (await parseOrThrow(response)) as PlanReviewRun;
  } catch (cause) {
    // On an ok response the only thing that can throw is reading the body,
    // which means the stream dropped after the server answered. On an HTTP
    // error parseOrThrow reads the body defensively and throws its own
    // Error, which passes through unchanged.
    if (response.ok) throw new PlanReviewConnectionError(cause);
    throw cause;
  }
}

/**
 * The create request got no response. The server may well have queued the
 * run anyway (Heroku logs this as H27), so the caller should look for it
 * before treating the start as failed.
 */
export class PlanReviewConnectionError extends Error {
  readonly cause: unknown;

  constructor(cause: unknown) {
    super("The connection dropped before the server answered");
    this.name = "PlanReviewConnectionError";
    this.cause = cause;
  }
}

export async function listPlanReviews(params?: {
  municipalityId?: number;
  scope?: PlanReviewListScope;
}) {
  const query = new URLSearchParams();
  if (params?.municipalityId) query.set("municipality_id", String(params.municipalityId));
  if (params?.scope) query.set("scope", params.scope);
  const url = `/api/v1/plan_review/reviews${query.toString() ? `?${query.toString()}` : ""}`;
  const response = await fetch(url);
  return parseOrThrow(response) as Promise<PlanReviewListItem[]>;
}

export async function getPlanReview(reviewId: number) {
  const response = await fetch(`/api/v1/plan_review/reviews/${reviewId}`);
  return parseOrThrow(response) as Promise<PlanReviewRun>;
}

export async function runPlanReviewOrdinanceCompliance(
  reviewId: number,
  params: { document_ids: number[]; desired_zoning?: string },
) {
  const response = await fetch(`/api/v1/plan_review/reviews/${reviewId}/run_ordinance_compliance`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({
      document_ids: params.document_ids,
      desired_zoning: params.desired_zoning,
    }),
  });
  return parseOrThrow(response) as Promise<PlanReviewRun>;
}

export async function savePlanReviewItemFeedback(params: {
  reviewId: number;
  itemKey: string;
  correctedStatus: "present" | "missing" | "uncertain";
  correctedExplanation?: string;
  correctedValue?: string;
  correctedEvidence?: PlanReviewEvidence[];
  correctionReason?: PlanReviewCorrectionReason;
  note?: string;
}) {
  const response = await fetch(`/api/v1/plan_review/reviews/${params.reviewId}/item_feedbacks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({
      item_feedback: {
        item_key: params.itemKey,
        corrected_status: params.correctedStatus,
        corrected_explanation: params.correctedExplanation,
        corrected_value: params.correctedValue,
        corrected_evidence: params.correctedEvidence || [],
        correction_reason: params.correctionReason,
        note: params.note,
      },
    }),
  });

  return parseOrThrow(response) as Promise<PlanReviewRun>;
}

export async function deletePlanReviewItemFeedback(params: { reviewId: number; feedbackId: number }) {
  const response = await fetch(`/api/v1/plan_review/reviews/${params.reviewId}/item_feedbacks/${params.feedbackId}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken() },
  });

  return parseOrThrow(response) as Promise<PlanReviewRun>;
}
